// Game math shared by the server (which decides every result) and the browser (which only displays it).

export const COINFLIP_MULTIPLIER = 1.96
export const DICE_RETURN = 98
export const DICE_MIN_TARGET = 2
export const DICE_MAX_TARGET = 95
export const HOUSE_EDGE = 0.03
export const MINES_TILES = 25
export const MINES_MIN = 1
export const MINES_MAX = 24
export const MINES_DEFAULT = 3

// The rocket's multiplier grows by e^(CRASH_GROWTH * seconds): 2× after about 7.7 seconds.
export const CRASH_GROWTH = 0.09
export const CRASH_MAX = 10_000
export const CRASH_MIN_AUTO = 1.01

export function floor2(value: number): number {
    return Math.floor(value * 100 + 1e-9) / 100
}

// Exact whole-dollar payout for a stake and a multiplier (kept to 4 decimals).
export function payoutFor(amount: number, multiplier: number): number {
    const basisPoints = Math.round(multiplier * 10_000)

    return Number((BigInt(amount) * BigInt(basisPoints)) / BigInt(10_000))
}

export function diceMultiplier(target: number): number {
    return Math.floor((DICE_RETURN / target) * 10_000) / 10_000
}

export function dicePayout(amount: number, target: number): number {
    return Number((BigInt(amount) * BigInt(DICE_RETURN)) / BigInt(target))
}

export function minesMultiplier(mines: number, revealed: number): number {
    if (revealed <= 0) return 1

    let multiplier = 1 - HOUSE_EDGE

    for (let i = 0; i < revealed; i++) {
        multiplier *= (MINES_TILES - i) / (MINES_TILES - mines - i)
    }

    return floor2(multiplier)
}

export function crashMultiplierAt(elapsedMs: number): number {
    return Math.exp((CRASH_GROWTH * Math.max(0, elapsedMs)) / 1000)
}

export function crashTimeFor(multiplier: number): number {
    return (Math.log(Math.max(1, multiplier)) / CRASH_GROWTH) * 1000
}

/* Limbo, Slide and Upgrader: P(result >= target) = 0.97 / target */

export const LIMBO_MIN = 1.01
export const LIMBO_MAX = 10_000

export function limboChance(target: number): number {
    return (1 - HOUSE_EDGE) / target
}

export const UPGRADER_MIN = 1.1
export const UPGRADER_MAX = 100

export function upgraderChance(multiplier: number): number {
    return (1 - HOUSE_EDGE) / multiplier
}

/* Keno: 40 numbers, 10 drawn, pick 1 to 10. Row = how many picked, column = how many hit. */

export const KENO_NUMBERS = 40
export const KENO_DRAWN = 10
export const KENO_MAX_PICKS = 10

export const KENO_PAYTABLE: number[][] = [
    [],
    [0, 3.96],
    [0, 1.9, 4.5],
    [0, 1, 3.1, 10.4],
    [0, 0, 2.2, 7.9, 90],
    [0, 0, 1.5, 4.2, 13, 300],
    [0, 0, 1.1, 2, 6.2, 100, 700],
    [0, 0, 1.1, 1.6, 3.5, 15, 225, 700],
    [0, 0, 1.1, 1.5, 2, 5.5, 39, 100, 800],
    [0, 0, 1.1, 1.3, 1.7, 2.5, 7.5, 50, 250, 1000],
    [0, 0, 1.1, 1.2, 1.3, 1.8, 3.5, 13, 50, 250, 1000]
]

/* Plinko: each row the ball goes left or right; the slot is the number of rights. */

export type PlinkoRisk = 'low' | 'medium' | 'high'

export const PLINKO_ROWS = [8, 12, 16] as const

export const PLINKO_TABLES: Record<number, Record<PlinkoRisk, number[]>> = {
    8: {
        low: [5.6, 2.1, 1.1, 1, 0.5, 1, 1.1, 2.1, 5.6],
        medium: [13, 3, 1.3, 0.7, 0.4, 0.7, 1.3, 3, 13],
        high: [29, 4, 1.5, 0.3, 0.2, 0.3, 1.5, 4, 29]
    },
    12: {
        low: [10, 3, 1.6, 1.4, 1.1, 1, 0.5, 1, 1.1, 1.4, 1.6, 3, 10],
        medium: [33, 11, 4, 2, 1.1, 0.6, 0.3, 0.6, 1.1, 2, 4, 11, 33],
        high: [170, 24, 8.1, 2, 0.7, 0.2, 0.2, 0.2, 0.7, 2, 8.1, 24, 170]
    },
    16: {
        low: [16, 9, 2, 1.4, 1.4, 1.2, 1.1, 1, 0.5, 1, 1.1, 1.2, 1.4, 1.4, 2, 9, 16],
        medium: [110, 41, 10, 5, 3, 1.5, 1, 0.5, 0.3, 0.5, 1, 1.5, 3, 5, 10, 41, 110],
        high: [1000, 130, 26, 9, 4, 2, 0.2, 0.2, 0.2, 0.2, 0.2, 2, 4, 9, 26, 130, 1000]
    }
}

/* Roulette: single zero, European wheel order. */

export const ROULETTE_WHEEL = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26]

export const ROULETTE_RED = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]

export type RouletteBetType = 'number' | 'red' | 'black' | 'odd' | 'even' | 'low' | 'high' | 'dozen' | 'column'

export type RouletteBet = {
    type: RouletteBetType
    value?: number
    amount: number
}

export function rouletteColor(n: number): 'green' | 'red' | 'black' {
    if (n === 0) return 'green'

    return ROULETTE_RED.includes(n) ? 'red' : 'black'
}

// Total return including the stake: a straight number returns 36×, even-money bets 2×, dozens and columns 3×.
export function rouletteReturn(type: RouletteBetType): number {
    if (type === 'number') return 36
    if (type === 'dozen' || type === 'column') return 3

    return 2
}

export function rouletteWins(bet: RouletteBet, n: number): boolean {
    if (bet.type === 'number') return bet.value === n
    if (n === 0) return false

    switch (bet.type) {
        case 'red': return rouletteColor(n) === 'red'
        case 'black': return rouletteColor(n) === 'black'
        case 'odd': return n % 2 === 1
        case 'even': return n % 2 === 0
        case 'low': return n <= 18
        case 'high': return n >= 19
        case 'dozen': return Math.ceil(n / 12) === bet.value
        case 'column': return (n % 3 === 0 ? 3 : n % 3) === bet.value
        default: return false
    }
}

/* Cards: rank 1 (ace) to 13 (king), suit 0 to 3. */

export const RANK_LABELS = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']
export const SUIT_SYMBOLS = ['♠', '♥', '♦', '♣']

export function cardPoints(rank: number): number {
    if (rank === 1) return 11

    return Math.min(10, rank)
}

export function handValue(cards: Array<{ r: number }>): { total: number, soft: boolean } {
    let total = 0
    let aces = 0

    for (const card of cards) {
        total += cardPoints(card.r)

        if (card.r === 1) aces++
    }

    while (total > 21 && aces > 0) {
        total -= 10
        aces--
    }

    return { total, soft: aces > 0 }
}

export function isBlackjack(cards: Array<{ r: number }>): boolean {
    return cards.length === 2 && handValue(cards).total === 21
}

/* Hi-Lo: at an ace "higher" means strictly higher, at a king "lower" means strictly lower. */

export type HiloGuess = 'higher' | 'lower'

export const HILO_MAX = 10_000

export function hiloChance(rank: number, guess: HiloGuess): number {
    if (guess === 'higher') return rank === 1 ? 12 / 13 : (14 - rank) / 13

    return rank === 13 ? 12 / 13 : rank / 13
}

export function hiloStep(rank: number, guess: HiloGuess): number {
    return Math.floor((0.99 / hiloChance(rank, guess)) * 10_000) / 10_000
}

export function hiloWins(rank: number, next: number, guess: HiloGuess): boolean {
    if (guess === 'higher') return rank === 1 ? next > 1 : next >= rank

    return rank === 13 ? next < 13 : next <= rank
}

/* Chicken: cross lanes, each with a fixed chance of getting hit. */

export type ChickenLevel = 'easy' | 'medium' | 'hard' | 'hardcore'

export const CHICKEN_LEVELS: Record<ChickenLevel, { lanes: number, risk: number, label: string }> = {
    easy: { lanes: 24, risk: 0.04, label: 'Easy' },
    medium: { lanes: 22, risk: 0.12, label: 'Medium' },
    hard: { lanes: 20, risk: 0.2, label: 'Hard' },
    hardcore: { lanes: 15, risk: 0.4, label: 'Hardcore' }
}

export function chickenMultiplier(level: ChickenLevel, lane: number): number {
    if (lane <= 0) return 1

    return floor2((1 - HOUSE_EDGE) / Math.pow(1 - CHICKEN_LEVELS[level].risk, lane))
}
